/* eslint-disable react-hooks/rules-of-hooks */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router";
import { all_routes } from "../../router/all_routes";
import CollapseHeader from "../../../core/common/collapse-header";
import {
  fetchUser,
  getUserPrivilege,
  saveUserPrivilege,
} from "../../../api/api";
import { useTranslation } from "react-i18next";
import { usePrivileges } from "../../../core/context/PrivilegeContext";

const route = all_routes;

interface UserType {
  ID: number;
  Name: string;
}

const UserPrivilege = () => {
  const { setPrivileges } = usePrivileges();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [users, setUsers] = useState<UserType[]>([]);
  const [selectedUser, setSelectedUser] = useState<number | null>(null);

  // ================= ADMIN CHECK =================
  const token = localStorage.getItem("token");

  let isAdmin = false;

  if (token) {
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      isAdmin = payload?.isAdmin === true;
    } catch (err) {
      console.error("Invalid token", err);
    }
  }

  useEffect(() => {
    if (!isAdmin) {
      alert("You have no access to this page");
      navigate(route.leadsDashboard);
    }
  }, [isAdmin, navigate]);

  if (!isAdmin) return null;

  // ================= PRIVILEGES =================
  const privilegeSections = [
    {
      title: "Leads",
      privileges: [
        { key: "open_leads", label: "Open Leads" },
        { key: "save_leads", label: "Save Leads" },
        { key: "update_leads", label: "Update Leads" },
        { key: "delete_leads", label: "Delete Leads" },
      ],
    },

    {
      title: "Orders",
      privileges: [
        { key: "open_orderform", label: "Open Order Form" },
        { key: "open_orderlist", label: "Open Order List" },
        { key: "open_orderstatus", label: "Open Order Status" },
        {
          key: "open_orderupdatedhistory",
          label: "Open Order Updated History",
        },
        { key: "save_orderform", label: "Save Order Form" },
        { key: "update_orderform", label: "Update Order Form" },
        { key: "delete_orderform", label: "Delete Order Form" },
        {
          key: "order_status_update",
          label: "Order Status Update",
        },
      ],
    },

    {
      title: "Tasks",
      privileges: [
        { key: "create_task", label: "Create Task" },
        { key: "update_task", label: "Update Task" },
        { key: "delete_task", label: "Delete Task" },
      ],
    },
  ];

  const allPrivileges = privilegeSections.flatMap(
    (section) => section.privileges
  );

  const [selectedPrivileges, setSelectedPrivileges] = useState<
    Record<string, boolean>
  >(() => {
    const initial: Record<string, boolean> = {};

    allPrivileges.forEach((p) => {
      initial[p.key] = false;
    });

    return initial;
  });

  // ================= FETCH USERS =================
  const getUsers = async () => {
    try {
      const data = await fetchUser();

      const mappedUsers: UserType[] = data.map((u: any) => ({
        ID: u.mid,
        Name: u.Name,
      }));

      setUsers(mappedUsers);
    } catch (err) {
      console.error("Error loading users", err);
    }
  };

  useEffect(() => {
    getUsers();
  }, []);

  // ================= LOAD USER PRIVILEGES =================
  const loadUserPrivileges = async (userId: number) => {
    try {
      const result = await getUserPrivilege(userId);

      const savedList = result.privileges || [];

      const updated: Record<string, boolean> = {};

      allPrivileges.forEach((p) => {
        updated[p.key] = savedList.includes(p.key);
      });

      setSelectedPrivileges(updated);

      // Update global privilege context
      setPrivileges(savedList);
    } catch (err) {
      console.error("Error loading privileges", err);
    }
  };

  // ================= HANDLE TOGGLE =================
  const handlePrivilegeChange = async (privKey: string) => {
    if (!selectedUser) {
      alert("Please select a user first!");
      return;
    }

    const updated = {
      ...selectedPrivileges,
      [privKey]: !selectedPrivileges[privKey],
    };

    setSelectedPrivileges(updated);

    const selectedKeys = Object.keys(updated).filter(
      (key) => updated[key] === true
    );

    try {
      await saveUserPrivilege({
        userId: Number(selectedUser),
        username:
          users.find((u) => u.ID === selectedUser)?.Name || "",
        privileges: selectedKeys,
      });

      setPrivileges(selectedKeys);

      console.log("Saved Privileges:", selectedKeys);
    } catch (err) {
      console.error("Error saving privilege", err);
    }
  };

  return (
    <div>
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              {/* Page Header */}
              <div className="page-header">
                <div className="row align-items-center">
                  <div className="col-8">
                    <h4 className="page-title">
                      {t("userPrivilege")}
                    </h4>
                  </div>

                  <div className="col-4 text-end">
                    <div className="head-icons">
                      <CollapseHeader />
                    </div>
                  </div>
                </div>
              </div>

              {/* Settings Menu */}
              <div className="card">
                <div className="card-body pb-0 pt-2">
                  <ul className="nav nav-tabs nav-tabs-bottom">
                    <li className="nav-item me-3">
                      <Link
                        to={route.profile}
                        className="nav-link px-0 active"
                      >
                        <i className="ti ti-settings-cog" />{" "}
                        {t("generalSettings")}
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="row">
                {/* Sidebar */}
                <div className="col-xl-3 col-lg-12 theiaStickySidebar">
                  <div className="card">
                    <div className="card-body">
                      <div className="settings-sidebar">
                        <h4 className="fw-semibold mb-3">
                          {t("generalSettings")}
                        </h4>

                        <div className="list-group list-group-flush settings-sidebar">
                          <Link
                            to={route.notification}
                            className="fw-medium active"
                          >
                            {t("notifications")}
                          </Link>

                          <Link
                            to={route.languageSettings}
                            className="fw-medium"
                          >
                            {t("languageSettings")}
                          </Link>

                          <Link
                            to={route.userPrivilege}
                            className="fw-medium"
                          >
                            {t("userPrivilege")}
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Main Content */}
                <div className="col-xl-9 col-lg-12">
                  <div className="card">
                    <div className="card-body">
                      <h4 className="fw-semibold mb-4">
                        {t("userPrivilege")}
                      </h4>

                      {/* User Dropdown */}
                      <div className="mb-4">
                        <label className="fw-medium mb-2">
                          {t("selectUser")}
                        </label>

                        <select
                          className="form-select w-50"
                          value={selectedUser ?? ""}
                          onChange={(e) => {
                            const userId = e.target.value
                              ? Number(e.target.value)
                              : null;

                            setSelectedUser(userId);

                            if (userId) {
                              loadUserPrivileges(userId);
                            }
                          }}
                        >
                          <option value="">
                            {t("selectUserPlaceholder")}
                          </option>

                          {users.map((user) => (
                            <option
                              key={user.ID}
                              value={user.ID}
                            >
                              {user.Name}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Privilege Cards */}
                      <div className="row">
                        {privilegeSections.map((section) => (
                          <div
                            className="col-lg-4 col-md-6 d-flex"
                            key={section.title}
                          >
                            <div className="card border shadow-none flex-fill mb-4">
                              <div className="card-body">
                                <h5 className="fw-semibold mb-4">
                                  {section.title}
                                </h5>

                                {section.privileges.map((priv) => (
                                  <div
                                    key={priv.key}
                                    className="d-flex justify-content-between align-items-center mb-3"
                                  >
                                    <span
                                      style={{
                                        fontSize: "14px",
                                        fontWeight: 500,
                                      }}
                                    >
                                      {priv.label}
                                    </span>

                                    <div className="form-check form-switch m-0">
                                      <input
                                        className="form-check-input"
                                        type="checkbox"
                                        role="switch"
                                        checked={
                                          selectedPrivileges[
                                            priv.key
                                          ]
                                        }
                                        onChange={() =>
                                          handlePrivilegeChange(
                                            priv.key
                                          )
                                        }
                                      />
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                {/* Main Content */}
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* /Page Wrapper */}
    </div>
  );
};

export default UserPrivilege;


// import React, { useEffect, useState } from 'react';
// import { Navigate } from "react-router-dom";
// import { useNavigate } from "react-router-dom";
// import { Link } from 'react-router';
// import { all_routes } from '../../router/all_routes';
// import CollapseHeader from '../../../core/common/collapse-header';
// import { fetchUser, getUserPrivilege, saveUserPrivilege } from "../../../api/api";
// import { useTranslation } from "react-i18next";
// import { usePrivileges } from '../../../core/context/PrivilegeContext';


// const route = all_routes;

// interface UserType {
//   ID: number; // mid
//   Name: string;
// }



// const UserPrivilege = () => {
    
//     const { setPrivileges } = usePrivileges();
//    const { t } = useTranslation();
//    const navigate = useNavigate();
//    const privilegesList = [
//   { key: "open_orderform", label: "Order Form" },
//   { key: "open_orderlist", label: "Order List" },
//   { key: "open_orderstatus", label: "Order Status" },
//   { key: "open_orderupdatedhistory", label: "Order Updated History" },
//   { key: "open_leads", label: "Leads" },

//   { key: "save_leads", label: "Save Leads" },
//   { key: "update_leads", label: "Update Leads" },
//   { key: "delete_leads", label: "Delete Leads" },

//   { key: "save_orderform", label: "Save Order Form" },
//   { key: "update_orderform", label: "Update Order Form" },
//   { key: "delete_orderform", label: "Delete Order Form" },

//   { key: "order_status_update", label: "Order Status Update" },

//   { key: "create_task", label: "Create Task" },
//   { key: "update_task", label: "Update Task" },
//   { key: "delete_task", label: "Delete Task" },
// ];

//   const [users, setUsers] = useState<UserType[]>([]); 

// const [selectedUser, setSelectedUser] = useState<number | null>(null);


// const [selectedPrivileges, setSelectedPrivileges] = useState<Record<string, boolean>>(() => {
//   const initial: Record<string, boolean> = {};
//   privilegesList.forEach((p) => (initial[p.key] = false));
//   return initial;
// });

// //open the privillage page only for admin section
// // Check admin status from token
// const token = localStorage.getItem("token");

// let isAdmin = false;

//   if (token) {
//     try {
//       const payload = JSON.parse(atob(token.split(".")[1]));
//       console.log("TOKEN PAYLOAD:", payload);

//       isAdmin = payload?.isAdmin === true;
//     } catch (err) {
//       console.error("Invalid token", err);
//     }
//   }

//    // redirect to leads dashboard if not admin
//   useEffect(() => {
//     if (!isAdmin) {
//       alert("You have no access to this page");
//       navigate(route.leadsDashboard);
//     }
//   }, [isAdmin, navigate]);

//   // block UI
//   if (!isAdmin) return null;


  
// // Fetch users and map mid → ID
//   const getUsers = async () => {
//     try {
//       const data = await fetchUser(); // returns [{ mid, Name }, ...]
//       const mappedUsers: UserType[] = data.map((u: any) => ({
//         ID: u.mid,
//         Name: u.Name,
//       }));
//       setUsers(mappedUsers);
//     } catch (err) {
//       console.error("Error loading users", err);
//     }
//   };

//   // eslint-disable-next-line react-hooks/rules-of-hooks
//   useEffect(() => {
//     getUsers();
//   }, []);




// //to get one user privileges
// const loadUserPrivileges = async (userId: number) => {
//   try {
//     const result = await getUserPrivilege(userId); 
//     const savedList = result.privileges || [];

//     const updated: Record<string, boolean> = {};
//     privilegesList.forEach((p) => {
//       updated[p.key] = savedList.includes(p.key);
//     });

//     setSelectedPrivileges(updated);
//      //  Update global PrivilegeContext
//     setPrivileges(savedList); //fetching the privilegesof user fro the condition
//   } catch (err) {
//     console.error("Error loading privileges", err);
//   }
// };


//  const handlePrivilegeChange = async (privKey: string) => {
//   if (!selectedUser) {
//     alert("Please select a user first!");
//     return;
//   }

//   // Update local state
//   const updated = {
//     ...selectedPrivileges,
//     [privKey]: !selectedPrivileges[privKey],
//   };

//   setSelectedPrivileges(updated);

//   // Prepare ALL checked privileges
//   const selectedKeys = Object.keys(updated).filter(
//     (key) => updated[key] === true
//   );

//   try {
//     await saveUserPrivilege({
//       userId: Number(selectedUser),
//       username: users.find((u) => u.ID === selectedUser)?.Name || "",
//       privileges: selectedKeys,  //send all selected privileges
//     });

//      //UPDATE GLOBAL STATE (THIS IS THE FIX)
//     setPrivileges(selectedKeys);

//     console.log("Saved Privileges:", selectedKeys);
//   } catch (err) {
//     console.error("Error saving privilege", err);
//   }
// };

// return (
//       <div>
//     {/* Page Wrapper */}
//     <div className="page-wrapper">
//       <div className="content">
//         <div className="row">
//           <div className="col-md-12">
//             {/* Page Header */}
//             <div className="page-header">
//               <div className="row align-items-center">
//                 <div className="col-8">
//                   <h4 className="page-title">{t("settings")}</h4>
//                 </div>
//                 <div className="col-4 text-end">
//                   <div className="head-icons">
//                     <CollapseHeader />
//                   </div>
//                 </div>
//               </div>
//             </div>
//             {/* /Page Header */}
//             {/* Settings Menu */}
//             <div className="card">
//               <div className="card-body pb-0 pt-2">
//               <ul className="nav nav-tabs nav-tabs-bottom">
//                       <li className="nav-item me-3">
//                         <Link to={route.profile} className="nav-link px-0 active">
//                           <i className="ti ti-settings-cog" />{t("generalSettings")}
//                         </Link>
//                       </li>
//                       </ul>
//               </div>
//             </div>
//             {/* /Settings Menu */}
//             <div className="row">
//           <div className="col-xl-3 col-lg-12 theiaStickySidebar">
//             {/* Settings Sidebar */}
//             <div className="card">
//               <div className="card-body">
//                 <div className="settings-sidebar">
//                   <h4 className="fw-semibold mb-3">{t("generalSettings")}</h4>
//                   <div className="list-group list-group-flush settings-sidebar">
                  
//                     <Link to={route.notification} className="fw-medium active">
//                     {t("notifications")}
//                     </Link>
                    
//                     <Link to={route.languageSettings} className='fw-medium'>
//                       {t("languageSettings")}
//                     </Link>
//                     <Link to={route.userPrivilege} className='fw-medium'>
//                     {t("userPrivilege")}
//                     </Link>
//             </div>
//           </div>
//         </div>
//       </div>
//         {/* /Settings Sidebar */}
//       </div>
//       <div className="col-xl-9 col-lg-12">
//         {/* Settings Info */}
//         <div className="card">
//           <div className="card-body">
//             <h4 className="fw-semibold mb-3">  {t("userPrivilege")}</h4>
//             <div className="settings-form">
//               {/* General Notifications */}
//               <div className="border-bottom mb-3 pb-3">
//                 <h5 className="fw-semibold mb-1"></h5>

            
//             {/* User Dropdown */}
//         <div className="mb-3 mt-2">
//             <label className="fw-medium mb-1">{t("selectUser")}</label>
//               <select
//                 className="form-select w-50"
//                 value={selectedUser ?? ""}
//                  onChange={(e) => {
//     const userId = e.target.value ? Number(e.target.value) : null;
//     setSelectedUser(userId);

//     if (userId) {
//       loadUserPrivileges(userId);  //Load privileges for selected user
//     }
//   }}
//               >
//                 <option value="">{t("selectUserPlaceholder")}</option>
//                     {users.map((user) => (
//                 <option key={user.ID} value={user.ID}>
//                   {user.Name}
//                 </option>
//               ))}
//                 </select>
//             </div>
//             </div>
         
//           {/* /General Notifications */}
//           {/* Custom Notifications */}
//           <div className="border-bottom mb-3 pb-3">
//             <h5 className="fw-semibold mb-1">{t("customUserPrivileges")}</h5>
//             <p>
//              {t("selectWhenChangesOccur")}
//             </p>
//             </div>
//           <div className="table-responsive notificaion-table">
//             <table className="table table-borderless">
//               <thead>
//                 <tr>
//                   <th />
//                   <th>{t("open")}</th>
//                   {/* <th>Save</th>
//                   <th>Other</th> */}
//                 </tr>
//               </thead>
//              <tbody>
//             {privilegesList.map((priv) => (
//               <tr key={priv.key}>
//                 <td>{priv.label}</td>
//                 <td>
//                     <div className="form-check form-switch">
//                       <input
//                         className="form-check-input"
//                         type="checkbox"
//                         role="switch"
//                         checked={selectedPrivileges[priv.key]}
//                         onChange={() => handlePrivilegeChange(priv.key)}
//                       />
//                     </div>
//                   </td>
//                 </tr>
//               ))}
//             </tbody>

//             </table>
//           </div>
//           {/* /Custom Notifications */}
//         </div>
//       </div>
//     </div>
//     {/* /Settings Info */}
//   </div>
// </div>

//           </div>
//         </div>
//       </div>
//     </div>
//     {/* /Page Wrapper */}
//     {/* Delete Account */}
//     <div className="modal custom-modal fade" id="delete_account" role="dialog">
//       <div className="modal-dialog modal-dialog-centered">
//         <div className="modal-content">
//           <div className="modal-header border-0 m-0 justify-content-end">
//             <button className="btn-close" data-bs-dismiss="modal" aria-label="Close">	
//               <i className="ti ti-x" />
//             </button>
//           </div>
//           <div className="modal-body">
//             <div className="success-message text-center">
//               <div className="success-popup-icon">
//                 <i className="ti ti-trash-x" />
//               </div>
//               <h3>{t("deleteAccount")}</h3>
//               <p className="del-info">{t("deleteAccount")}</p>
//               <div className="col-lg-12 text-center modal-btn">
//                 <Link to="#" className="btn btn-light" data-bs-dismiss="modal">Cancel</Link>
//                 <Link to={route.security} className="btn btn-danger">Yes, Delete it</Link>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>
//     </div>
//     {/* /Delete Account */}
//   </div>
//   );
// };

// export default UserPrivilege;
